// Phase 0089 — Live Study History + supervisor verification queue.

import { useState } from 'react'
import {
  App, Button, Empty, Modal, Select, Space, Table, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { liveStudyApi, type StudySession } from '@/features/hr/liveStudyApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Paragraph } = Typography

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  IN_PROGRESS: 'blue',
  PAUSED: 'cyan',
  COMPLETED: 'gold',
  VERIFIED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
}

export default function LiveStudyHistoryPage() {
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [filter, setFilter] = useState<string | undefined>()
  const [verifyOpen, setVerifyOpen] = useState<StudySession | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['live-studies-history', filter],
    queryFn: () => liveStudyApi.history(filter, 100),
  })

  const verifyMut = useMutation({
    mutationFn: ({ id, approved, remarks }: { id: string; approved: boolean; remarks?: string }) =>
      liveStudyApi.verify(id, { approved, remarks }),
    onSuccess: () => {
      message.success('Recorded')
      setVerifyOpen(null); setRejectReason('')
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<StudySession> = [
    { title: 'Session', dataIndex: 'session_no', width: 180,
      render: (v: string) => <Text code>{v}</Text> },
    { title: 'Date', dataIndex: 'study_date', width: 110,
      render: (d: string) => dayjs(d).format('MMM D') },
    { title: 'Status', dataIndex: 'session_status', width: 140,
      render: (s: string) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    { title: 'Result', dataIndex: 'result_status', width: 150,
      render: (r: string | null) => r ? <Tag>{r}</Tag> : '—' },
    { title: 'CAPA', dataIndex: 'capa_required', width: 80,
      render: (b: boolean) => b ? <Tag color="orange">YES</Tag> : '' },
    { title: 'Retakes', dataIndex: 'retake_count', width: 90 },
    {
      title: 'Action', key: 'action', width: 220, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => navigate(`/hr/live-studies/sessions/${r.id}`)}>
            Open
          </Button>
          {r.session_status === 'COMPLETED' && (
            <Button size="small" type="primary" onClick={() => setVerifyOpen(r)}>
              Verify
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Study History"
        subtitle="All live study sessions you opened, supervised or owned"
        extra={
          <Space>
            <Select allowClear placeholder="Status filter" style={{ width: 180 }}
                    onChange={setFilter}
                    options={[
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'PAUSED', label: 'Paused' },
                      { value: 'COMPLETED', label: 'Awaiting Verification' },
                      { value: 'VERIFIED', label: 'Verified' },
                      { value: 'REJECTED', label: 'Rejected' },
                    ]} />
            <Button onClick={() => navigate('/hr/live-studies')}>Back to Studies</Button>
          </Space>
        }
      />
      {rows.length === 0 ? (
        <Empty description="No sessions yet" />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={rows}
               loading={isLoading} pagination={{ pageSize: 30 }}
               scroll={{ x: 1000 }} />
      )}

      <Modal
        open={!!verifyOpen}
        title={`Verify ${verifyOpen?.session_no || ''}`}
        onCancel={() => setVerifyOpen(null)}
        footer={
          <Space>
            <Button onClick={() => setVerifyOpen(null)}>Cancel</Button>
            <Button danger onClick={() => verifyOpen && verifyMut.mutate({
              id: verifyOpen.id, approved: false, remarks: rejectReason,
            })}>
              Reject
            </Button>
            <Button type="primary"
                    onClick={() => verifyOpen && verifyMut.mutate({
                      id: verifyOpen.id, approved: true, remarks: rejectReason,
                    })}>
              Approve
            </Button>
          </Space>
        }
      >
        <Paragraph>Result status: <Text strong>{verifyOpen?.result_status || '—'}</Text></Paragraph>
        <Paragraph>Gap found: <Text strong>{verifyOpen?.gap_found ? 'YES' : 'NO'}</Text></Paragraph>
        <Paragraph>CAPA required: <Text strong>{verifyOpen?.capa_required ? 'YES' : 'NO'}</Text></Paragraph>
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          style={{ width: '100%', marginTop: 8, padding: 8 }}
          placeholder="Remarks (required for reject)"
        />
      </Modal>
    </>
  )
}
